import { format, parseISO, isSameDay, isToday } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Calendar, Plus, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CalendarEvent } from '@/hooks/useOutlookCalendar';
import { CalendarEventCard } from './CalendarEventCard';

interface CalendarSidebarProps {
  selectedDate: Date;
  events: CalendarEvent[];
  onDeleteEvent: (eventId: string) => void;
  onCreateEvent: () => void;
}

export function CalendarSidebar({ 
  selectedDate, 
  events, 
  onDeleteEvent,
  onCreateEvent 
}: CalendarSidebarProps) {
  const dayEvents = events.filter(event => {
    if (!event.start_time) return false;
    try {
      const eventDate = parseISO(event.start_time);
      return isSameDay(eventDate, selectedDate);
    } catch {
      return false;
    }
  }).sort((a, b) => {
    const aTime = a.start_time ? new Date(a.start_time).getTime() : 0;
    const bTime = b.start_time ? new Date(b.start_time).getTime() : 0;
    return aTime - bTime;
  });

  const isSelectedToday = isToday(selectedDate);

  return (
    <div className="flex flex-col h-full bg-card rounded-xl border border-border overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-gradient-to-r from-energy-green/5 to-energy-blue/5">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-energy-green" />
              <h3 className="font-semibold text-lg">
                {format(selectedDate, 'EEEE', { locale: nl })}
              </h3>
              {isSelectedToday && (
                <span className="px-2 py-0.5 rounded-full bg-energy-green text-white text-xs font-medium">
                  Vandaag
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              {format(selectedDate, 'd MMMM yyyy', { locale: nl })}
            </p>
          </div>
          <Button 
            size="sm" 
            onClick={onCreateEvent}
            className="bg-energy-green hover:bg-energy-green/90"
          >
            <Plus className="h-4 w-4 mr-1" />
            Nieuw
          </Button>
        </div>
      </div>

      {/* Events list */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-3">
          {dayEvents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                <Sparkles className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground font-medium">
                Geen afspraken
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Klik op "Nieuw" om een afspraak toe te voegen
              </p>
            </div>
          ) : (
            dayEvents.map(event => (
              <CalendarEventCard 
                key={event.id} 
                event={event} 
                onDelete={onDeleteEvent}
              />
            ))
          )}
        </div>
      </ScrollArea>

      {/* Footer summary */}
      {dayEvents.length > 0 && (
        <div className="p-3 border-t border-border bg-muted/30">
          <p className="text-sm text-muted-foreground text-center">
            {dayEvents.length} afspra{dayEvents.length === 1 ? 'ak' : 'ken'} gepland
          </p>
        </div>
      )}
    </div>
  );
}
